import { getNotificationTranslation } from "@repo/types";
import { formatDate } from "@repo/utils/date";
import i18n, { type TFunction } from "i18next";
import { toDateLocale } from "@/utils/date-locale";

/**
 * Localized title/body for a notification. Typed notifications render
 * `notifications.<type>.*`; legacy rows (no known `type`) keep the stored text.
 */
export function translateNotification(
  t: TFunction,
  notification: { title: string; content: string; metadata?: Record<string, unknown> | null }
): { title: string; content: string } {
  const typed = getNotificationTranslation(notification.metadata);
  if (!typed) return { title: notification.title, content: notification.content };

  const { params, documentKey } = typed;
  const document = documentKey
    ? t(`notifications.documents.${documentKey}`, { defaultValue: params.document })
    : params.document;
  const uploaderType = params.uploaderType
    ? t(`notifications.uploaderTypes.${params.uploaderType}`, { defaultValue: "" })
    : "";
  const uploader =
    params.uploaderType === "ADMIN"
      ? uploaderType
      : [uploaderType, params.uploaderName].filter(Boolean).join(" ") ||
        t("notifications.uploaderTypes.UNKNOWN", { defaultValue: "" });
  const reason = params.rejectionReason
    ? t(`document.rejectReason.${params.rejectionReason}`, {
        defaultValue: params.rejectionReason,
      })
    : "";
  const expiresAt = formatDate(params.expiresAt, {
    locale: toDateLocale(i18n.language),
    fallback: "",
  });
  const values = {
    document,
    uploader,
    reason,
    driver: params.driverName,
    expiresAt,
    defaultValue: "",
  };

  return {
    title: t(`${typed.key}.title`, values) || notification.title,
    content: t(`${typed.key}.body`, values) || notification.content,
  };
}
